import React, { useState } from "react"
import { Button, Form, Table } from "react-bootstrap"
import { useDispatch, useSelector } from "react-redux"
import { Formik } from "formik"

import { getDefaultRateCategories, getField } from "@/onboarding/features/product/selectors"
import {
  appendFieldListItem,
  removeFieldListItem,
  updateFieldListItem,
} from "@/onboarding/features/product/Slice"
import { newComponent, getComponentType } from "@/onboarding/features/product/models/Component"
import TextDisplay from "@/onboarding/features/product/components/shared/TextDisplay"
import RateInputs from "@/onboarding/features/product/components/shared/RateInputs"

const ComponentEditForm = ({ component, index, onCancel, fieldName }): JSX.Element => {
  const dispatch = useDispatch()

  return (
    <Formik
      enableReinitialize
      initialValues={component}
      onSubmit={(values, { setSubmitting }) => {
        setSubmitting(true)
        dispatch(updateFieldListItem({ fieldName, index, value: values }))
        setSubmitting(false)
        onCancel()
      }}
    >
      {({ handleSubmit, isSubmitting }) => (
        <Form className="d-flex flex-column gap-3" onSubmit={handleSubmit}>
          <RateInputs name="ratesheet" />
          <div className="d-flex gap-3">
            <Button type="submit" disabled={isSubmitting}>
              Submit
            </Button>
            <Button variant="danger" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  )
}

const Edit = ({ fieldName }): JSX.Element => {
  const [editingComponent, setEditingComponent] = useState(null)
  const dispatch = useDispatch()
  const field = useSelector(getField(fieldName))
  const components = field.value
  const defaultRateCategories = useSelector(getDefaultRateCategories)

  const addComponent = () => {
    dispatch(
      appendFieldListItem({
        fieldName,
        value: newComponent({ defaultRateCategories, kind: getComponentType(fieldName) }),
      })
    )
  }

  const cancelEdit = () => setEditingComponent(null)

  if (editingComponent !== null) {
    return (
      <ComponentEditForm
        component={components[editingComponent]}
        fieldName={fieldName}
        index={editingComponent}
        onCancel={cancelEdit}
      />
    )
  }

  return (
    <>
      <Table>
        <thead>
          <tr>
            <th>Name</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {components.length === 0 && (
            <tr>
              <td colSpan={2}>No components loaded</td>
            </tr>
          )}
          {components.map((component, index) => (
            <tr key={component.id}>
              <td>
                <TextDisplay value={component.name} />
              </td>
              <td>
                <div className="d-flex justify-content-end gap-3">
                  <span className="cursor-pointer" onClick={() => setEditingComponent(index)}>
                    <i className="fas fa-edit" />
                  </span>
                  <span
                    className="cursor-pointer text-danger"
                    onClick={() =>
                      dispatch(
                        removeFieldListItem({
                          fieldName,
                          index,
                        })
                      )
                    }
                  >
                    <i className="fas fa-trash" />
                  </span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <Button size="sm" variant="outline-primary" onClick={addComponent}>
        <i className="fas fa-plus me-2" />
        Add Component
      </Button>
    </>
  )
}

export default Edit
